
import type { StarterCoordinationRunCreateRequest } from "../services/starterCoordination";
import { downloadRunJson } from "../services/runExport";
import { ProtectionSwitch } from "../components/ProtectionSwitch";
import { RunTraceabilityPanel } from "../components/RunTraceabilityPanel";
import { SldLinkPanel } from "../components/SldLinkPanel";
import { StarterCoordinationForm } from "../components/StarterCoordinationForm";
import { StarterCoordinationResultPanel } from "../components/StarterCoordinationResultPanel";
import { StarterCoordinationResultSummary } from "../components/StarterCoordinationResultSummary";
import { StarterCoordinationWarningPanel } from "../components/StarterCoordinationWarningPanel";
import { StudyProjectLine } from "../components/StudyProjectLine";
import { useSldElements } from "../hooks/useSldElements";
import { useStarterCoordination } from "../hooks/useStarterCoordination";

function describeError(error: unknown): string {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }
  return "The starter coordination study could not be completed.";
}

// Study-page layout (Master Prompt v2.1 section 19): collapsible inputs beside
// a result-first column - summary, warnings, traceability, then detail tables.
// EOS-05 is delivered in parts (A20, A21); this page is 05a, the first, and the
// protection switch moves to the breaker duty and selectivity page (05b).
export function StarterCoordinationPage() {
  const { calculate, reset, result, run, error, isPending, isError } = useStarterCoordination();
  // The active revision's single-line diagram, for the bus and motor pickers (A27 (b)).
  const { elements: sldElements } = useSldElements();

  // Errors are surfaced through the hook state below, not thrown into the form.
  async function handleSubmit(payload: StarterCoordinationRunCreateRequest): Promise<void> {
    try {
      await calculate(payload);
    } catch {
      // Intentionally empty: isError / error already describe the failure.
    }
  }

  return (
    <main data-starter-coordination-page>
      <ProtectionSwitch />
      <header>
        <p>EOS-05 · Protection coordination</p>
        <h1>Starter coordination (Type 1 / Type 2)</h1>
        <p>
          Check each motor starter's declared Type 1 or Type 2 coordination evidence against
          the fault level of a stored short-circuit study of the same project revision, using
          the validated KES Electrical OS calculation service. Coordination itself is proven
          only by the manufacturer's test certificate.
        </p>
      </header>

      <div data-study-layout>
        <section aria-labelledby="starter-coordination-inputs-heading">
          <details open data-study-inputs>
            <summary>
              <h2 id="starter-coordination-inputs-heading">Inputs</h2>
            </summary>
            <StudyProjectLine />
            <StarterCoordinationForm
              disabled={isPending}
              onSubmit={handleSubmit}
              sldElements={sldElements}
            />
          </details>
        </section>

        <section aria-labelledby="starter-coordination-results-heading" data-study-results>
          <h2 id="starter-coordination-results-heading">Results</h2>

          {isPending ? (
            <p role="status" data-calculation-state="pending">
              Checking the starter coordination…
            </p>
          ) : null}

          {isError ? (
            <p role="alert" data-calculation-state="error">
              {describeError(error)}
            </p>
          ) : null}

          {result ? (
            <div data-calculation-state="success">
              <StarterCoordinationResultSummary result={result} />
              {run ? <SldLinkPanel link={run.sld_link} /> : null}
              <StarterCoordinationWarningPanel result={result} />
              {run ? (
                <RunTraceabilityPanel
                  run={run}
                  onExport={() =>
                    downloadRunJson(
                      { run, result },
                      `${run.calculation_key}-rev${run.revision_number}.json`,
                    )
                  }
                />
              ) : null}
              <StarterCoordinationResultPanel result={result} />
              <p>
                <button type="button" onClick={reset}>
                  Clear results
                </button>
              </p>
            </div>
          ) : null}

          {!isPending && !isError && !result ? (
            <p data-calculation-state="idle">
              Submit the inputs to check the starter coordination.
            </p>
          ) : null}
        </section>
      </div>

      <section aria-labelledby="starter-coordination-review-heading">
        <h2 id="starter-coordination-review-heading">Engineering review required</h2>
        <p>
          Calculation results are engineering evidence, not an automatic compliance
          declaration. Confirm the linked fault study, every motor's full-load current and
          starting method, and each starter's certificate against the exact device
          combination before approval or issue.
        </p>
      </section>
    </main>
  );
}

export default StarterCoordinationPage;
